import React, { useRef, useState, forwardRef } from 'react';
import classNames from 'classnames';
import useControllableState from '../hooks/useControllableState';
import { useConfig } from '../ConfigProvider';
import DateTable from './tables/DateTable';
import MonthTable from './tables/MonthTable';
import YearTable from './tables/YearTable';

/**
 * CalendarBase is a custom component that wraps the DateTable, MonthTable, and YearTable components.
 * It uses the `forwardRef` function to pass the ref to the container.
 *
 * @param {object} props
 * @param {string} [props.className] - The class name of the calendar.
 * @param {number} [props.dateViewCount=1] - The date view count.
 * @param {string} [props.dayClassName] - The class name of the day.
 * @param {object} [props.dayStyle] - The style of the day.
 * @param {Date} [props.defaultMonth] - The default month.
 * @param {string} [props.defaultView='date'] - The default view.
 * @param {function} [props.disableDate] - The function to disable the date.
 * @param {boolean} [props.disableOutOfMonth] - The flag to disable the out of month.
 * @param {boolean} [props.enableHeaderLabel=true] - The flag to enable the header label.
 * @param {string} [props.firstDayOfWeek='monday'] - The first day of the week.
 * @param {boolean} [props.hideOutOfMonthDates] - The flag to hide the out of month dates.
 * @param {boolean} [props.hideWeekdays] - The flag to hide the weekdays.
 * @param {function} [props.isDateFirstInRange] - The function to check if the date is the first in the range.
 * @param {function} [props.isDateInRange] - The function to check if the date is in the range.
 * @param {function} [props.isDateLastInRange] - The function to check if the date is the last in the range.
 * @param {string} [props.labelFormat='MMMM YYYY'] - The label format.
 * @param {string} [props.locale] - The locale of the calendar.
 * @param {Date} [props.maxDate] - The max date.
 * @param {Date} [props.minDate] - The min date.
 * @param {Date} [props.month] - The month.
 * @param {string} [props.monthLabelFormat='MMM'] - The month label format.
 * @param {function} [props.onChange] - The function to call when the date changes.
 * @param {function} [props.onDayMouseEnter] - The function to call when the day is mouse entered.
 * @param {function} [props.onMonthChange] - The function to call when the month changes.
 * @param {number} [props.paginateBy=dateViewCount] - The paginate by.
 * @param {boolean} [props.preventFocus] - The flag to prevent the focus.
 * @param {boolean} [props.range] - The flag to check if the date is in the range.
 * @param {function} [props.renderDay] - The function to render the day.
 * @param {object} [props.style] - The style of the calendar.
 * @param {Date} [props.value] - The value of the calendar.
 * @param {string} [props.weekdayLabelFormat='dd'] - The weekday label format.
 * @param {string} [props.weekendDays] - The weekend days.
 * @param {string} [props.yearLabelFormat='YYYY'] - The year label format.
 * @returns {React.ReactElement}
 */
const CalendarBase = forwardRef((props, ref) => {
    const {
        className,
        dateViewCount = 1,
        dayClassName,
        dayStyle,
        defaultMonth,
        defaultView = 'date',
        disableDate,
        disableOutOfMonth,
        enableHeaderLabel = true,
        firstDayOfWeek = 'monday',
        hideOutOfMonthDates,
        hideWeekdays,
        isDateFirstInRange,
        isDateInRange,
        isDateLastInRange,
        labelFormat = 'MMMM YYYY',
        locale,
        maxDate,
        minDate,
        month,
        monthLabelFormat = 'MMM',
        onChange,
        onDayMouseEnter,
        onMonthChange,
        paginateBy = dateViewCount,
        preventFocus,
        range,
        renderDay,
        style,
        value,
        weekdayLabelFormat = 'dd',
        weekendDays,
        yearLabelFormat = 'YYYY',
        ...rest
    } = props;

    const { locale: themeLocale } = useConfig();

    const [selectionState, setSelectionState] = useState(defaultView);

    const finalLocale = locale || themeLocale;

    const daysRefs = useRef(
        Array(dateViewCount)
            .fill(0)
            .map(() => [])
    );

    const [_month, setMonth] = useControllableState({
        prop: month,
        defaultProp: defaultMonth !== undefined ? defaultMonth : new Date(),
        onChange: onMonthChange,
    });

    const [yearSelection, setYearSelection] = useState(_month.getFullYear());
    const [monthSelection, setMonthSelection] = useState(_month.getMonth());

    const minYear = minDate instanceof Date ? minDate.getFullYear() : 100;
    const maxYear = maxDate instanceof Date ? maxDate.getFullYear() : 10000;

    const daysPerRow = 6;

    const focusOnNextFocusableDay = (direction, monthIndex, payload, n = 1) => {
        const changeRow = ['down', 'up'].includes(direction);

        const rowIndex = changeRow
            ? payload.rowIndex + (direction === 'down' ? n : -n)
            : payload.rowIndex;

        const cellIndex = changeRow
            ? payload.cellIndex
            : payload.cellIndex + (direction === 'right' ? n : -n);

        const dayToFocus = daysRefs.current[monthIndex][rowIndex][cellIndex];

        if (!dayToFocus) {
            return;
        }

        if (dayToFocus.disabled) {
            focusOnNextFocusableDay(direction, monthIndex, payload, n + 1);
        } else {
            dayToFocus.focus();
        }
    };

    const handleDayKeyDown = (monthIndex, payload, event) => {
        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();

                const hasRowBelow =
                    payload.rowIndex + 1 < daysRefs.current[monthIndex].length;
                if (hasRowBelow) {
                    focusOnNextFocusableDay('down', monthIndex, payload);
                }
                break;
            }
            case 'ArrowUp': {
                event.preventDefault();

                const hasRowAbove = payload.rowIndex > 0;
                if (hasRowAbove) {
                    focusOnNextFocusableDay('up', monthIndex, payload);
                }
                break;
            }
            case 'ArrowRight': {
                event.preventDefault();

                const isNotLastCell = payload.cellIndex !== daysPerRow;
                if (isNotLastCell) {
                    focusOnNextFocusableDay('right', monthIndex, payload);
                } else if (monthIndex + 1 < dateViewCount) {
                    if (daysRefs.current[monthIndex + 1][payload.rowIndex]) {
                        daysRefs.current[monthIndex + 1][
                            payload.rowIndex
                        ][0]?.focus();
                    }
                }
                break;
            }
            case 'ArrowLeft': {
                event.preventDefault();

                if (payload.cellIndex !== 0) {
                    focusOnNextFocusableDay('left', monthIndex, payload);
                } else if (monthIndex > 0) {
                    if (daysRefs.current[monthIndex - 1][payload.rowIndex]) {
                        daysRefs.current[monthIndex - 1][payload.rowIndex][
                            daysPerRow
                        ].focus();
                    }
                }
                break;
            }
            default:
                break;
        }
    };

    return (
        <div className={classNames('picker-view', className)} {...rest}>
            {selectionState === 'year' && (
                <YearTable
                    value={yearSelection}
                    minYear={minYear}
                    maxYear={maxYear}
                    onChange={(year) => {
                        setMonth(new Date(year, monthSelection, 1));
                        setYearSelection(year);
                        setSelectionState('date');
                    }}
                    className={className}
                    preventFocus={preventFocus}
                    yearLabelFormat={yearLabelFormat}
                />
            )}
            {selectionState === 'month' && (
                <MonthTable
                    value={{
                        month: _month.getMonth(),
                        year: _month.getFullYear(),
                    }}
                    year={yearSelection}
                    onYearChange={setYearSelection}
                    onNextLevel={() => setSelectionState('year')}
                    locale={finalLocale}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={(monthValue) => {
                        setMonth(new Date(yearSelection, monthValue, 1));
                        setMonthSelection(monthValue);
                        setSelectionState('date');
                    }}
                    className={className}
                    style={style}
                    preventFocus={preventFocus}
                    yearLabelFormat={yearLabelFormat}
                    monthLabelFormat={monthLabelFormat}
                />
            )}
            {selectionState === 'date' && (
                <DateTable
                    dateViewCount={dateViewCount}
                    paginateBy={paginateBy}
                    month={_month}
                    locale={finalLocale}
                    minDate={minDate}
                    maxDate={maxDate}
                    enableHeaderLabel={enableHeaderLabel}
                    daysRefs={daysRefs}
                    onMonthChange={setMonth}
                    onNextLevel={(view) => setSelectionState(view)}
                    onDayKeyDown={handleDayKeyDown}
                    style={style}
                    dayClassName={dayClassName}
                    dayStyle={dayStyle}
                    disableOutOfMonth={disableOutOfMonth}
                    disableDate={disableDate}
                    hideWeekdays={hideWeekdays}
                    preventFocus={preventFocus}
                    firstDayOfWeek={firstDayOfWeek}
                    value={value}
                    range={range}
                    onChange={onChange}
                    labelFormat={labelFormat}
                    weekdayLabelFormat={weekdayLabelFormat}
                    onDayMouseEnter={onDayMouseEnter}
                    renderDay={renderDay}
                    hideOutOfMonthDates={hideOutOfMonthDates}
                    isDateInRange={isDateInRange}
                    isDateFirstInRange={isDateFirstInRange}
                    isDateLastInRange={isDateLastInRange}
                    weekendDays={weekendDays}
                />
            )}
        </div>
    );
});

export default CalendarBase;
